/**
 *
 * @module component/extender
 * @author shojib [mahmud.hossain@mlb.com]
 * @description component data extender
 *
 */

import assign from 'lodash-es/assign';
import get from 'lodash-es/get';
import pageMeta from 'pageMeta';
import HORIZONTAL_CONTROLS_BY_MODE_AND_DEVICE from '../../json/horizontal-controls-by-mode-and-device.json';

/**
 * @function hasPostseasonGames
 * @param data
 * @returns {boolean}
 */
function hasPostseasonGames(data = {}, gameUtils) {

    const dates = data.dates || [];
    let hasPostseasonGame = false;

    dates.forEach(date => {
        const games = date.games || [];
        games.forEach(game => {
            const { gameType, tiebreaker } = game;
            const { isPostSeason } = gameUtils.getPostSeasonReady(gameType, tiebreaker);
            const isPostSeasonReady = isPostSeason;
            if (isPostSeasonReady) {
                hasPostseasonGame = true;
            }
        });
    });

    return hasPostseasonGame;
}

/**
 * @function shouldUseControls
 * @desc Return true if the mode & device combination uses them
 * @param opts
 * @returns {boolean}
 */
function shouldUseControls(opts = {}, data = {}) {

    const { mode, device } = opts;

    const controlsByDevice = HORIZONTAL_CONTROLS_BY_MODE_AND_DEVICE[mode];
    let shouldUse = !!(controlsByDevice && controlsByDevice[device]);

    if (mode === 'MIG') {

        if (!data.totalItems || data.totalItems < 2) {

            shouldUse = false;

        }
    }

    return shouldUse;

}

/**
 * @function normalizeSchedule
 * @desc Create a shell schedule response with schedule data
 * @param schedule
 */
function normalizeSchedule(schedule = {}) {

    const normalizedSchedule = {
        totalItems: 0,
        totalEvents: 0,
        totalGames: 0,
        totalGamesInProgress: 0,
        dates: []
    };

    return assign(normalizedSchedule, schedule);

}

/**
 * @function removeFuturePromos
 * @desc Removes promotions from future game data for MiLB MIG
 * @param {Object} schedule
 * @returns {Object} schedule
 */
function removeFuturePromos(schedule = {}) {
    let counter = 0;
    if (schedule.dates) {
        schedule.dates.map((date) => {
            if (!date.games) {
                return date;
            }
            return date.games.map((game) => {
                if (counter > 0) {
                    game.promotions = [];
                }
                counter++;
                return game;
            });
        });
    }

    return schedule;
}

/**
 * @function getSelectedMigGameIndex
 * @desc Find which game should be focused as "current game"
 * @param prevSchedule
 * @param nextSchedule
 * @returns {Number} Index of game to be displayed
 */
function getSelectedMigGameIndex(prevSchedule = {}, nextSchedule = {}) {

    const numNextGames = nextSchedule.totalGames;

    if (numNextGames > 0) {

        if (numNextGames > 1) {
            const todayGames = get(nextSchedule, 'dates[0].games', {});
            const nextGame = get(nextSchedule, 'dates[0].games[0]', {});
            const { status = {}, doubleHeader = 'N' } = nextGame;
            const { codedGameState } = status;

            //if nextSchedule game 1 is cancelled / ppd, if midnight, go to next game
            if (codedGameState === 'C' || codedGameState === 'D') {

                const { serverTime } = pageMeta;
                const nextGameDate = get(nextSchedule, 'dates[0].date');
                const compDate = new Date(`${nextGameDate}T23:59:59-04:00`);
                const serverDate = new Date(serverTime);

                if (serverDate > compDate) {

                    return prevSchedule.totalGames + 1;

                }

            }

            // makeup games sometimes aren't coded as doubleheader
            // switch to next game when past Preview state
            if (doubleHeader !== 'N' || todayGames.length > 1) {
                const secondGameAbstractGameCode = get(todayGames, '[1].status.abstractGameCode', '');
                if (secondGameAbstractGameCode && secondGameAbstractGameCode !== 'P') {
                    return prevSchedule.totalGames + 1;
                }
            }

        }

        return prevSchedule.totalGames;

    }

    return Math.max(prevSchedule.totalGames - 1, 0);

}

/**
 * @function mergeMigData
 * @description converts `previousGameSchedule` & `nextGameSchedule` into 1 schedule response
 * @param data
 * @returns {{totalItems: *, totalEvents: *, totalGames: *, totalGamesInProgress: *, dates: *, selectedGameIdx: Number}}
 */
function mergeMigData(data = {}, sport) {

    let prevGameSchedule = get(data, 'teams[0].previousGameSchedule', {});
    let nextGameSchedule = get(data, 'teams[0].nextGameSchedule', {});

    if (sport && sport === 'milb') {
        nextGameSchedule = removeFuturePromos(nextGameSchedule);
    }

    prevGameSchedule = normalizeSchedule(prevGameSchedule);
    nextGameSchedule = normalizeSchedule(nextGameSchedule);

    const selectedGameIdx = getSelectedMigGameIndex(prevGameSchedule, nextGameSchedule);

    return {
        totalItems: prevGameSchedule.totalItems + nextGameSchedule.totalItems,
        totalEvents: prevGameSchedule.totalEvents + nextGameSchedule.totalEvents,
        totalGames: prevGameSchedule.totalGames + nextGameSchedule.totalGames,
        totalGamesInProgress: prevGameSchedule.totalGamesInProgress + nextGameSchedule.totalGamesInProgress,
        dates: prevGameSchedule.dates.concat(nextGameSchedule.dates),
        selectedGameIdx
    };

}

/**
 *
 * @function extender
 * @param {Object} data
 * @param {Object} opts shared options
 * @returns {Object} extended data
 *
 */
function extender(data = {}, opts = {}) {

    const { gameUtils, mode, dictionary, mlbMoment, date, mlbLinker, lang, isPostseasonPush, showPostseasonPushSponsorLogo, ppSponsorURL, sport, defaultPromo } = opts;
    const moment = mlbMoment.getMomentTimezone();

    const hasPostseasonGame = hasPostseasonGames(data, gameUtils);

    if (mode === 'MIG') {

        // eslint-disable-next-line no-param-reassign
        data = mergeMigData(data, sport);

    } else if (mode === 'responsive' && sport === 'mlb') {

        if (data.dates && data.dates.length > 0) {

            data.dates.forEach((d) => {
                if (d.events) {
                    d.events = d.events.filter(event => event.name.indexOf('All-Star Workout Day: Home Run Derby') > -1);
                }
            });

        }

    }

    const text = {
        scoresHeader: (dictionary.mini_scoreboard_heading_allscores || 'All Scores'),
        prevGame: dictionary.scores_prevgame || 'Prev Game',
        nextGame: dictionary.scores_nextgame || 'Next Game'
    };

    const scoresMode = {
        isGameSelector: mode === 'game-selector',
        isResponsive: mode === 'responsive',
        isMinisb: mode === 'minisb',
        isMIG: mode === 'MIG',
        isPostSeason: mode === 'postseason'
    };

    const useControls = shouldUseControls(opts, data);

    let prevDate = '';
    let nextDate = '';
    let scoresLink = '';
    let dateFormat = 'dddd';
    let headerConfig = {};

    if (mode === 'minisb') {

        prevDate = mlbLinker.getLink(`/scores/${moment(date).subtract(1, 'days').format('YYYY-MM-DD')}`, true);
        nextDate = mlbLinker.getLink(`/scores/${moment(date).add(1, 'days').format('YYYY-MM-DD')}`, true);
        scoresLink = mlbLinker.getLink(`/scores/${moment(date).format('YYYY-MM-DD')}`, true);
        headerConfig = {
            className: (isPostseasonPush) ? ' postseason-push' : '',
            showPostseasonPushSponsorLogo,
            ppSponsorURL
        };

        if (lang !== 'en') {

            dateFormat = 'ddd';

        }

    }

    return assign(data, {
        date: moment(date).format(dateFormat),
        dateShort: moment(date).format('ddd'),
        prevDate,
        nextDate,
        scoresLink,
        text,
        useControls,
        mode: scoresMode,
        headerConfig,
        defaultPromo,
        hasPostseasonGame
    });

}

export default extender;
